import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { FileText, Upload, ClipboardList, CircleCheckBig } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '~/components/ui';
import { DocumentFileRow } from '~/pages/order-view/sections/DocumentFileRow';
import { UploadDocumentModal } from '~/components/cards/UploadDocumentModal';
import {
  useDeleteOrderDocument,
  useDownloadOrderDocument,
} from '~/api/orderDocument';
import type { OrderDocumentType } from '~/utils/documentType';
import type { RootState } from '~/redux/store';
import { formatCurrency, formatDateTime } from '~/utils';
import type { GetOrderByIdQuery, GetPackingListQuery } from '~/graphql/graphql';

type PackingListItemData = NonNullable<
  NonNullable<GetPackingListQuery['packingList']>['items'][number]
>;

type OrderItem = Pick<
  NonNullable<NonNullable<GetOrderByIdQuery['order']>['items'][number]>,
  'platformProductsLengths' | 'quantity' | 'price'
>;

interface OrderDocumentsCardProps {
  orderId: string;
  documents: OrderDocumentType[];
  packingList?: {
    updatedAt?: string | null | undefined;
    notes?: string | null;
    items: PackingListItemData[];
  } | null;
  orderItems?: OrderItem[];
  originalTotal?: number;
  packedTotal?: number;
  variance?: number;
  currency?: string;
}

export function OrderDocumentsCard({
  orderId,
  documents,
  packingList,
  orderItems,
  originalTotal,
  packedTotal,
  variance,
  currency,
}: Readonly<OrderDocumentsCardProps>) {
  const { t } = useTranslation('order');
  const role = useSelector((state: RootState) => state.auth.user?.role);
  const ownUploadType = role ? `${role}_UPLOAD` : null;
  const [showUploadModal, setShowUploadModal] = useState(false);
  const hasUploadedPackingList = documents.some(
    (doc) =>
      doc.documentType === 'PACKING_LIST' && doc.uploadType === 'SELLER_UPLOAD'
  );

  const {
    mutate: deleteDocument,
    isPending: isDeletingDocument,
    variables: deletingVariables,
  } = useDeleteOrderDocument();

  const {
    mutate: downloadDocument,
    isPending: isDownloadingDocument,
    variables: downloadingVariables,
  } = useDownloadOrderDocument();

  const handleDownload = (documentId: string) => {
    downloadDocument(
      { documentId },
      {
        onError: () => {
          toast.error(t('order.documents.download_error'));
        },
      }
    );
  };

  const handleDelete = (documentId: string) => {
    deleteDocument(
      { orderId, documentId },
      {
        onSuccess: () => {
          toast.success(t('order.documents.delete_success'));
        },
        onError: () => {
          toast.error(t('order.documents.delete_error'));
        },
      }
    );
  };

  const orderItemMap = new Map(
    (orderItems ?? []).map((oi) => [oi.platformProductsLengths.id, oi])
  );

  return (
    <>
      <div className="bg-neut-0 rounded-lg border border-neut-200 shadow-sm overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-neut-100">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-2xl flex items-center justify-center bg-prim-50">
                <FileText size={20} className="text-prim" aria-hidden="true" />
              </div>
              <div>
                <h2 className="text-neut-prim font-semibold">
                  {t('order.documents.title')}
                </h2>
                <p className="text-sm text-neut-sec mt-1 max-w-2xl">
                  {t('order.documents.description')}
                </p>
              </div>
            </div>
            <div className="flex flex-col items-start gap-3 lg:items-end">
              <Button
                size="sm"
                width="auto"
                onClick={() => setShowUploadModal(true)}
              >
                <Upload size={16} aria-hidden="true" />
                {t('order.documents.upload_button')}
              </Button>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">
          {!hasUploadedPackingList && packingList != null ? (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
              {/* Header */}
              <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-prim-50">
                    <ClipboardList
                      size={18}
                      className="text-prim"
                      aria-hidden="true"
                    />
                  </div>
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-sm font-semibold text-neut-prim">
                        {t('order.packing_list.heading')}
                      </h3>
                      <span className="rounded-full px-2.5 py-1 text-xs font-medium bg-green-100 text-green-700">
                        {t('order.packing_list.confirmed_status')}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-neut-sec">
                      {t('order.packing_list.confirmed_description')}
                    </p>
                  </div>
                </div>
                <div className="inline-flex items-center gap-2 rounded-full bg-green-100 px-3 py-1.5 text-xs font-medium text-green-700 flex-shrink-0">
                  <CircleCheckBig size={14} aria-hidden="true" />
                  {packingList.updatedAt != null
                    ? t('order.packing_list.confirmed_at', {
                        at: formatDateTime(packingList.updatedAt),
                      })
                    : t('order.packing_list.confirmed_status')}
                </div>
              </div>

              {/* Table + stats */}
              <div className="mt-4 space-y-4">
                {/* Comparison table */}
                <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                  <div className="hidden lg:grid grid-cols-12 gap-3 border-b border-gray-100 bg-gray-50 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    <div className="col-span-6">
                      {t('order.packing_list.item_column')}
                    </div>
                    <div className="col-span-3 text-right">
                      {t('order.packing_list.ordered_column')}
                    </div>
                    <div className="col-span-3 text-right">
                      {t('order.packing_list.loaded_column')}
                    </div>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {packingList.items.map((packItem) => {
                      const orderedItem = orderItemMap.get(
                        packItem.platformProductsLengths.id
                      );
                      const orderedQty = orderedItem?.quantity.value ?? null;
                      const orderedUnit = orderedItem?.quantity.unit ?? null;
                      return (
                        <div
                          key={packItem.id}
                          className="grid grid-cols-1 gap-3 px-4 py-4 lg:grid-cols-12 lg:items-center"
                        >
                          <div className="lg:col-span-6">
                            <p className="text-sm font-medium text-neut-prim">
                              {packItem.platformProductsLengths.platformProduct
                                .name ?? 'Unknown Product'}
                            </p>
                            <p className="mt-1 text-xs text-neut-sec">
                              {packItem.platformProductsLengths
                                .lengthDisplayValue ?? ''}
                            </p>
                          </div>
                          <div className="flex items-center justify-between text-sm lg:col-span-3 lg:block lg:text-right">
                            <span className="text-xs font-semibold uppercase tracking-wide text-gray-400 lg:hidden">
                              {t('order.packing_list.ordered_column')}
                            </span>
                            <span className="text-gray-700">
                              {orderedQty != null
                                ? `${orderedQty} ${orderedUnit ?? ''}`
                                : '—'}
                            </span>
                          </div>
                          <div className="lg:col-span-3">
                            <div className="flex items-center justify-between text-sm lg:block lg:text-right">
                              <span className="text-xs font-semibold uppercase tracking-wide text-gray-400 lg:hidden">
                                {t('order.packing_list.loaded_column')}
                              </span>
                              <span className="font-medium text-neut-prim">
                                {packItem.quantity.value}{' '}
                                {packItem.quantity.unit}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Stat cards */}
                <div className="grid gap-3 lg:grid-cols-3">
                  <div className="rounded-xl border border-gray-200 bg-white p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                      {t('order.packing_list.original_total')}
                    </p>
                    <p className="mt-1 text-lg font-semibold text-neut-prim">
                      {formatCurrency(originalTotal ?? 0, currency)}
                    </p>
                  </div>
                  <div className="rounded-xl border border-gray-200 bg-white p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                      {t('order.packing_list.packed_total')}
                    </p>
                    <p className="mt-1 text-lg font-semibold text-neut-prim">
                      {formatCurrency(packedTotal ?? 0, currency)}
                    </p>
                  </div>
                  <div className="rounded-xl border border-gray-200 bg-white p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                      {t('order.packing_list.variance')}
                    </p>
                    <p
                      className={`mt-1 text-lg font-semibold ${
                        (variance ?? 0) > 0
                          ? 'text-green-600'
                          : (variance ?? 0) < 0
                            ? 'text-red-600'
                            : 'text-neut-prim'
                      }`}
                    >
                      {(variance ?? 0) >= 0 ? '+' : ''}
                      {formatCurrency(variance ?? 0, currency)}
                    </p>
                  </div>
                </div>

                {/* Packing Notes */}
                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                    {t('order.packing_list.notes_label')}
                  </p>
                  <p className="rounded-xl border border-gray-200 bg-white p-4 text-sm text-neut-sec">
                    {packingList.notes != null && packingList.notes.length > 0
                      ? packingList.notes
                      : t('order.packing_list.notes_empty')}
                  </p>
                </div>
              </div>
            </div>
          ) : !hasUploadedPackingList ? (
            <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-5">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-emerald-100">
                  <ClipboardList
                    size={18}
                    className="text-emerald-600"
                    aria-hidden="true"
                  />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">
                    {t('order.packing_list.heading')}
                  </h3>
                  <p className="mt-1 text-xs text-neut-500">
                    {t('order.packing_list.not_available')}
                  </p>
                  <p className="mt-0.5 text-xs text-neut-400">
                    {t('order.packing_list.not_available_description')}
                  </p>
                </div>
              </div>
            </div>
          ) : null}

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm text-neut-prim font-semibold">
                {t('order.documents.shared_files')}
              </h3>
              <span className="text-xs text-neut-sec">
                {t('order.documents.document_count', {
                  count: documents.length,
                })}
              </span>
            </div>

            {documents.length === 0 ? (
              <div className="rounded-xl border border-dashed border-neut-200 p-8 text-center">
                <p className="text-sm font-medium text-neut-prim">
                  {t('order.documents.no_documents_title')}
                </p>
                <p className="mt-1 text-xs text-neut-sec">
                  {t('order.documents.no_documents_description')}
                </p>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {documents.map((document) => (
                  <DocumentFileRow
                    key={document.id}
                    file={document}
                    onDownload={() => handleDownload(document.id)}
                    onDelete={
                      document.uploadType === ownUploadType
                        ? () => handleDelete(document.id)
                        : undefined
                    }
                    isDownloading={
                      isDownloadingDocument &&
                      downloadingVariables?.documentId === document.id
                    }
                    isDeleting={
                      isDeletingDocument &&
                      deletingVariables?.documentId === document.id
                    }
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <UploadDocumentModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        orderId={orderId}
      />
    </>
  );
}
